import { Component, inject, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { ErrorControlDirective } from '../error-control.component';
import { FormGroupDirective, NgForm, UntypedFormControl, ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { UpdateOption } from '@vendasta/listing-products';
import { GalaxyAiIconService } from '@vendasta/galaxy/ai-icon';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import {
  UpdateRequest,
  UpdateOption as GlxyUpdateOption,
} from '@vendasta/galaxy/ai-text-button/src/ai-text-button.component';

@Component({
  selector: 'forms-input-textarea-ai',
  templateUrl: './input-textarea-ai.component.html',
  styleUrls: ['./input-textarea-ai.component.scss'],
  standalone: false,
})
export class InputTextareaAiComponent extends ErrorControlDirective implements OnInit, OnDestroy {
  @Input() control: UntypedFormControl;
  @Input() tooltip: string;
  @Input() placeholder: string;
  @Input({ required: true }) maxLength!: number;
  @Input() required: boolean;
  @Input() fieldName: string;
  @Input() fieldKey: string;
  @Input() fetchSuggestion: (fieldKey: string) => Observable<string>;
  @Input() fetchUpdateSuggestion: (
    fieldKey: string,
    existing: string,
    option: UpdateOption,
    tone?: string,
  ) => Observable<string>;

  // Register the galaxy-ai-icon with the icon registry.
  galaxyAiIconService = inject(GalaxyAiIconService);

  subscriptions: Subscription[] = [];
  suggestions: string[] = [];
  selectedIndex = 0;

  isLoading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    @Optional() _parentForm: NgForm,
    @Optional() _parentFormGroup: FormGroupDirective,
    private translateService: TranslateService,
  ) {
    super(_parentForm, _parentFormGroup);
  }

  getSuggestion(): void {
    this.isLoading$$.next(true);
    this.subscriptions.push(
      this.fetchSuggestion(this.fieldKey).subscribe((resp: string) => {
        this.control.setValue(resp);
        this.control.markAsDirty();
        this.isLoading$$.next(false);
      }),
    );
  }

  suggestUpdate(req: UpdateRequest): void {
    this.isLoading$$.next(true);
    this.subscriptions.push(
      this.fetchUpdateSuggestion(
        this.fieldKey,
        this.control.value,
        convertOption(req.option),
        req.tone?.name,
      ).subscribe((resp: string) => {
        this.control.setValue(resp);
        this.control.markAsDirty();
        this.isLoading$$.next(false);
      }),
    );
  }

  ngOnInit(): void {
    if (!this.errorMessages.maxlength) {
      this.errorMessages.maxlength = (err: ValidationErrors) => {
        return this.translateService.instant('FRONTEND.FORMS.INPUT.MAXIMUM_LENGTH', { maxLength: err.requiredLength });
      };
    }

    if (!this.errorMessages.minlength) {
      this.errorMessages.minlength = (err: ValidationErrors) => {
        return this.translateService.instant('FRONTEND.FORMS.INPUT.MINIMUM_LENGTH', { minLength: err.requiredLength });
      };
    }
  }
  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  protected readonly UpdateOption = UpdateOption;
}

function convertOption(option: GlxyUpdateOption): UpdateOption {
  switch (option) {
    case GlxyUpdateOption.UNSPECIFIED:
      return UpdateOption.UPDATE_OPTION_UNSPECIFIED;
    case GlxyUpdateOption.IMPROVE_WRITING:
      return UpdateOption.UPDATE_OPTION_IMPROVE_WRITING;
    case GlxyUpdateOption.FIX_SPELLING_AND_GRAMMAR:
      return UpdateOption.UPDATE_OPTION_SPELLING_AND_GRAMMAR;
    case GlxyUpdateOption.MAKE_SHORTER:
      return UpdateOption.UPDATE_OPTION_MAKE_SHORTER;
    case GlxyUpdateOption.OPTIMIZE_BY_KEYWORDS:
      return UpdateOption.UPDATE_OPTION_OPTIMIZE_BY_KEYWORDS;
    case GlxyUpdateOption.CHANGE_TONE:
      return UpdateOption.UPDATE_OPTION_CHANGE_TONE;
  }
}
